import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";

import { Card, CardHeader, CardContent, CardActions, Button, Typography, Box, Grid, TextField, InputAdornment, IconButton, Autocomplete, SelectChangeEvent, Chip, Checkbox, Badge, } from "@mui/material";
import { LocationOn, Phone, Info, LocationOnOutlined, FilterAltOffOutlined, HighlightOffOutlined, } from "@mui/icons-material";

import { RootState } from "../../../../redux/store";
import NoDataFound from "../../../../components/common/table/NoDataFoundModule";
import CustomPagination from "../../../../components/common/table/TablePagination";
import ThreeCardSkeleton from "./ThreeCardSkeleton";
import { openDrawer } from "../../../../redux/reducer/drawerSlice";
import { FetchCityListService, FetchPreHospitalListService, FetchServicesListService } from "../../../../utils/services/refer.service";
import { BaseUrls } from "../../../../utils/base-urls";
import { clearSelectedHospitals, deselectHospital, selectHospital } from "../../../../redux/reducer/HospitalSelectionSlice";
import { FetchSchemeListService } from "../../../../utils/services/scheme.service";
import { Hospital, Scheme } from "../../../../utils/dto/module/scheme.types";
import HospitalTypeChip, { HospitalType } from "./HospitalTypeChip";

const SearchHospital = () => {
    const { searchTerm } = useSelector((state: RootState) => state.filters);
    const dispatch = useDispatch();
    const [cityName, setCityName] = React.useState<{ id: number; city: string }>({ id: 0, city: "" });
    const [HospitalTypeData, setHospitalTypeData] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false)
    const [schemes, setSchemes] = useState<Scheme[]>([]);
    const [hospitalList, setHospitalList] = React.useState<Hospital[]>([])
    const [selectedServices, setSelectedServices] = React.useState<string[]>([]);
    const [selectedScheme, setSelectedScheme] = React.useState<number[]>([]);
    const [services, setServices] = React.useState<string[]>([]);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [cityList, setCityList] = useState<any[]>([]);
    const selectedHospitals = useSelector((state: RootState) => state.hospitalSelection.selectedHospitals);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [payload, setPayload] = useState<{ offset: number; limit: number }>({
        offset: 0,
        limit: 6,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 800);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    const handleServiceChange = (event: any, newValue: string[]) => {
        setSelectedServices(newValue);
    };

    const fetcSchemeListService = async () => {
        const { code, data } = await FetchSchemeListService({});
        if (code === 200 && data && data.schemes) {
            setSchemes(data.schemes)
        }
    }
    const fetchCityListData = async () => {
        const { code, data } = await FetchCityListService({ type: "city" });
        if (code === 200 && data && data.unique_cities) {
            setCityList(data.unique_cities)
        }
    }

    const fetchServicesListService = async (query: string) => {
        const { code, data } = await FetchServicesListService({
            limit: 20,
            offset: 0,
            services: query,
            city: cityName.city
        });
        if (code === 200 && data && data.unique_services) {
            setServices(data.unique_services);
        }
    };

    const handleInputChange = (event: React.SyntheticEvent, value: string) => {
        if (value.length > 2) {
            fetchServicesListService(value);
        }
    };

    useEffect(() => {
        fetchServicesListService('');
    }, [cityName])

    useEffect(() => {
        fetcSchemeListService();
        fetchCityListData();
    }, [])

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPayload((prev) => ({ ...prev, offset: (newPage - 1) * prev.limit }));
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent) => {
        setPayload((prev) => ({ ...prev, limit: Number(event.target.value), offset: 0, }));
    };
    const FetchHospitalListData = useCallback(() => {
        setLoading(true);

        const payloadData = {
            city: cityName.city,
            limit: payload.limit,
            offset: payload.offset,
            search: debouncedSearchTerm.trim(),
            status: "approved",
            schemes: selectedScheme.length > 0 ? selectedScheme : null,
            hospital_type: HospitalTypeData?.join(",") || "",
            services: selectedServices?.join(",") || "",
        };
        FetchPreHospitalListService(payloadData)
            .then(({ code, data }) => {
                if (code === 200 && data?.hospitals) {
                    setHospitalList(data.hospitals);
                    setTotalCount(data.total_count ?? 0);
                } else {
                    setHospitalList([]);
                    setTotalCount(0);
                }
            })
            .catch(() => {
                setHospitalList([]);
                setTotalCount(0);
            })
            .finally(() => setLoading(false));
    }, [HospitalTypeData, cityName, payload, debouncedSearchTerm, selectedScheme, selectedServices]);

    useEffect(() => {
        FetchHospitalListData();
    }, [cityName, debouncedSearchTerm, payload, selectedScheme, selectedServices, HospitalTypeData]);


    const handleViewDetails = (hospital: any) => {
        dispatch(openDrawer({ title: `🩺 ${hospital?.hospital_name} Details`, _Key: 'viewHospitalDetails', isFullScreen: false, drawerData: hospital, drawerWidth: 'lg' }));
    };

    const handleReset = () => {
        setSelectedServices([]);
        setSelectedScheme([]);
        setHospitalTypeData([]);
    }

    const handleSelectHospital = (hospital: Hospital) => {
        if (selectedHospitals.some(h => h.id === hospital.id)) {
            dispatch(deselectHospital(hospital.id));
        } else {
            dispatch(selectHospital(hospital));
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", p: 1, px:3 }} >
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                        options={cityList}
                        freeSolo
                        disableClearable
                        value={cityName}
                        onInputChange={(event, newValue) => {
                            const selectedCity = cityList.find(city => city.city === newValue);
                            setCityName(
                                selectedCity ? selectedCity : { id: 0, city: newValue }
                            );
                        }}
                        getOptionLabel={(option: any) => option?.city ?? ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                // type="search"
                                InputLabelProps={{ className: params.InputLabelProps?.className ?? "" }}
                                size="small"
                                placeholder="Search City"
                                fullWidth
                                variant="outlined"

                                InputProps={{
                                    ...params.InputProps,

                                    startAdornment: (
                                        <InputAdornment position="end">
                                            <LocationOnOutlined color="primary" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} display="flex" alignItems="center" gap={1}>
                                <LocationOnOutlined color="primary" />
                                <Typography>{option.city}</Typography>
                            </Box>
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={HospitalType}
                        getOptionLabel={(option) => option.label}
                        value={HospitalType.filter((type) => HospitalTypeData.includes(type.value))}
                        onChange={(event, newValue) =>
                            setHospitalTypeData(newValue.map((item) => item.value))
                        }
                        limitTags={1}
                        renderTags={(selected, getTagProps) =>
                            selected.map((option, index) => (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    label={option.label}
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params as any}
                                label="Hospital Type"
                                placeholder="Select Hospital Type"
                                fullWidth
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                }}
                            />
                        )}
                    />

                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        size="small"
                        multiple
                        freeSolo
                        limitTags={1}
                        options={services}
                        value={selectedServices}
                        onChange={handleServiceChange}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        onInputChange={handleInputChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={'Search Services & Diseases'}
                                label={'Services & diseases'}
                                fullWidth
                                size="small"
                                InputLabelProps={{ className: params.InputLabelProps?.className ?? "" }}

                            />
                        )}
                    />
                </Grid>
                <Grid item xs={11} sm={5} md={3}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={schemes}
                        limitTags={1}
                        getOptionLabel={(option) => option.scheme_name}
                        value={schemes.filter((scheme) => selectedScheme.includes(scheme.id))}
                        onChange={(event, newValue) => setSelectedScheme(newValue.map((item) => item.id))}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    label={option.scheme_name}
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={'Schemes'}
                                fullWidth={true}
                                size={"small" as "small" | "medium"}
                                InputLabelProps={{ className: params.InputLabelProps?.className ?? "" }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={1} sm={1} md={2} display={"flex"} justifyContent={"center"} alignItems={"center"} gap={1}>
                    <IconButton
                        color="primary"
                        disabled={!selectedScheme.length && !selectedServices.length && !HospitalTypeData.length}
                        onClick={() => handleReset()}
                    >
                        <FilterAltOffOutlined />
                    </IconButton>
                    <Badge color="error" badgeContent={selectedHospitals ? selectedHospitals.length : 0} max={9}>
                        <Button variant="outlined" size="small" color="error" onClick={() => dispatch(clearSelectedHospitals())} endIcon={<HighlightOffOutlined />}>Hospital  </Button>
                    </Badge>
                </Grid>
            </Grid>
            
            {loading && <ThreeCardSkeleton />}
            {!loading && hospitalList.length > 0 ? (
                <>
                    <Box sx={{ maxHeight: 'calc(72vh - 180px)', overflowY: 'auto', minHeight: 'calc(72vh - 180px)' }}>
                        <Grid container spacing={2} >
                            {hospitalList.map((hospital, index) => (
                                <Grid item xs={12} sm={6} md={4} key={hospital.id}>
                                    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column", height: '100%', borderRadius: 1, boxShadow: 3 }} >
                                        <CardHeader
                                            avatar={<img src={hospital.logo ? (hospital.upload_type === "Manual" ? `${BaseUrls.S3_BASE_URL.url + hospital.logo}` : hospital.logo) : "/images/arogya-logo.png"} alt={hospital.hospital_name} style={{ height: "40px", width: "auto", borderRadius: 6 }} />}
                                            title={hospital.hospital_name} subheader={`Pincode: ${hospital.pincode}`} sx={{ p: 1 }}
                                            action={
                                                <Checkbox
                                                    checked={selectedHospitals.some(h => h.id === hospital.id)}
                                                    onChange={() => handleSelectHospital(hospital)}
                                                    color="info"
                                                    sx={{
                                                        color: selectedHospitals.some(h => h.id === hospital.id) ? "info.main" : "grey.500",
                                                        "&.Mui-checked": {
                                                            color: "info.main",
                                                        },
                                                        transform: "scale(1.2)",
                                                        mx: 1,
                                                    }}
                                                />
                                            }
                                        />

                                        <CardContent sx={{ flex: 1, p: 1 }}>
                                            <Typography variant="body2">
                                                <LocationOn sx={{ verticalAlign: "middle", color: "tomato", mb: 1 }} /> {hospital?.address}, {hospital?.city}
                                            </Typography>
                                            <Typography variant="body2">
                                                <Phone sx={{ verticalAlign: "middle", mb: 1, color: 'text.primary' }} /> {hospital?.contact_number || '-- N/A --'}
                                            </Typography>
                                        </CardContent>
                                        <CardActions sx={{ justifyContent: "space-between", borderTop: "1px solid #e0e0e0" }}>
                                            <HospitalTypeChip type={hospital.hospital_type || 'government'} />
                                            <Button onClick={() => handleViewDetails(hospital)} startIcon={<Info fontSize="small" />} color="primary" size="small" variant="outlined" sx={{ borderRadius: 10, p: '5px 10px' }}>
                                                View Details
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    <CustomPagination
                        count={totalCount}
                        rowsPerPage={payload.limit}
                        page={payload.offset / payload.limit + 1}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[6, 12, 16, 18]}
                    />
                </>
            ) : (
                <>
                    {!loading && <NoDataFound height='65vh' moduleName={'Hospital List'} />}
                </>
            )}
        </Box>
    );
};

export default SearchHospital;
